import { useState, useEffect } from "react";

import homeDwService from "@plannel/services/dashboard/home-dw-service";
import debug from "@plannel/utils/debug-util";

/*
  홈의 DW 응답 중 **여러 카드가 같은 응답을 나눠 쓰는** 것들을 요청 1회로 묶는다.

  왜 필요한가 — 카드마다 자기 useEffect 로 부르면 같은 엔드포인트가 카드 수만큼 호출된다.
    실측(랜딩 1회): rp-summary x3, bf-accuracy x2, demand-signals x2, inventory-plan x2,
    mp-production x2 → DW 집계 쿼리 6건이 매번 덤으로 돈다. "랜딩은 경량 조회만" 원칙과 어긋난다.

  왜 그냥 모듈 캐시가 아닌가 — 값을 계속 들고 있으면 홈에 다시 들어와도 갱신되지 않는다.
    그래서 **참조 카운트**를 센다: 첫 소비자가 붙을 때 한 번 부르고, 마지막 소비자가 떠날 때
    엔트리를 버린다. 홈을 벗어났다 다시 들어오면 카드가 새로 마운트되므로 다시 조회된다.
    (한 랜딩 안에서는 1회, 재진입하면 다시 1회 — 중복 제거와 신선도를 둘 다 만족한다.)

  fetcher 를 인자로 받지 않고 레지스트리로 두는 이유는 훅 의존성을 key 하나로 고정하기 위해서다.
*/
const FETCHERS = {
  bfAccuracy: () => homeDwService.getBfAccuracy(),
  inventoryPlan: () => homeDwService.getInventoryPlan(),
  rpSummary: () => homeDwService.getRpSummary(),
  mpProduction: () => homeDwService.getMpProduction(),
  demandSignals: () => homeDwService.getDemandSignals(),
};

const EMPTY = { loaded: false, data: null };

// key → { refs, promise, result }
const inflight = new Map();

const useSharedDwRequest = (key) => {
  const [state, setState] = useState(() => inflight.get(key)?.result ?? EMPTY);

  useEffect(() => {
    let alive = true;
    let entry = inflight.get(key);

    if (!entry) {
      entry = { refs: 0, result: null, promise: null };
      inflight.set(key, entry);
      // 실패도 성공과 같은 모양으로 정착시킨다 — 카드는 loaded=true, data=null 을 보고 '—' 를 띄운다.
      //   여기서 reject 를 남기면 뒤늦게 붙는 소비자가 로딩 링에 갇힌다.
      entry.promise = FETCHERS[key]()
        .then((res) => ({ loaded: true, data: res?.data ?? null }))
        .catch((e) => { debug(e); return { loaded: true, data: null }; })
        .then((result) => { entry.result = result; return result; });
    }

    entry.refs += 1;
    entry.promise.then((result) => { if (alive) setState(result); });

    return () => {
      alive = false;
      entry.refs -= 1;
      // 마지막 소비자가 떠나면 버린다 — 다음 진입 때 정체된 값을 보여주지 않기 위해서다.
      if (entry.refs <= 0 && inflight.get(key) === entry) inflight.delete(key);
    };
  }, [key]);

  return state;
};

export default useSharedDwRequest;
